import logger from "../utils/logger";
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ClipboardList, Plus, Check, User, ChevronRight, Link2, Copy, Trash2, Send, MessageCircle, Mail as MailIcon, Sparkles, QrCode, Download } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

import API_URL from '../utils/api';

// Always build share URLs from the browser's current origin (proxies in cloud-hosted
// apps mangle the backend's Origin/Host headers, so trusting the server is unsafe).
const buildShareUrl = (token) => `${window.location.origin}/join/${token}`;
const withShareUrl = (inv) => (inv && inv.token ? { ...inv, share_url: buildShareUrl(inv.token) } : inv);

const Onboarding = () => {
    const { canManage } = useAuth();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'invites' ? 'invites' : 'records');
    const [records, setRecords] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [templateDialog, setTemplateDialog] = useState(false);
    const [assignDialog, setAssignDialog] = useState(false);
    const [templateForm, setTemplateForm] = useState({ name: '', items: '' });
    const [assignForm, setAssignForm] = useState({ employee_id: '', template_id: '' });
    const [expandedRecord, setExpandedRecord] = useState(null);

    // Invite Links state
    const [invites, setInvites] = useState([]);
    const [inviteDialog, setInviteDialog] = useState(false);
    const [generatedInvite, setGeneratedInvite] = useState(null); // shows the copy/share view after generation
    const [qrViewInvite, setQrViewInvite] = useState(null); // re-show QR for an existing invite
    const [inviteForm, setInviteForm] = useState({
        email: '',
        name_hint: '',
        role: 'employee',
        department: '',
        designation: '',
        expires_in_days: 7,
        send_email: false,
        is_multi_use: false,
        max_uses: 0,
    });

    const fetchData = useCallback(async () => {
        try {
            const requests = [
                axios.get(`${API_URL}/api/onboarding`, { withCredentials: true }),
                axios.get(`${API_URL}/api/onboarding/templates`, { withCredentials: true }),
            ];
            if (canManage) {
                requests.push(axios.get(`${API_URL}/api/employees`, { withCredentials: true }));
                requests.push(axios.get(`${API_URL}/api/onboarding/summary`, { withCredentials: true }));
                requests.push(axios.get(`${API_URL}/api/onboarding-invites`, { withCredentials: true }));
            }
            const results = await Promise.all(requests);
            setRecords(results[0].data);
            setTemplates(results[1].data);
            if (results[2]) setEmployees(results[2].data.filter(e => e.role !== 'admin'));
            if (results[3]) setSummary(results[3].data);
            if (results[4]) setInvites(results[4].data.map(withShareUrl));
        } catch (error) { logger.error(error); }
        finally { setLoading(false); }
    }, [canManage]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-open Generate Link dialog when arriving from /employees with ?action=generate
    useEffect(() => {
        if (canManage && searchParams.get('action') === 'generate') {
            setInviteDialog(true);
        }
    }, [canManage, searchParams]);

    const handleCreateTemplate = async (e) => {
        e.preventDefault();
        const items = templateForm.items.split('\n').map(i => i.trim()).filter(Boolean);
        if (items.length === 0) { toast.error('Add at least one item'); return; }
        try {
            await axios.post(`${API_URL}/api/onboarding/templates`, { name: templateForm.name, items }, { withCredentials: true });
            setTemplateDialog(false);
            setTemplateForm({ name: '', items: '' });
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };

    const handleAssign = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/onboarding/assign/${assignForm.employee_id}?template_id=${assignForm.template_id}`, {}, { withCredentials: true });
            setAssignDialog(false);
            setAssignForm({ employee_id: '', template_id: '' });
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };

    const toggleItem = async (recordId, itemIndex, completed) => {
        try {
            await axios.put(`${API_URL}/api/onboarding/${recordId}/item`, { item_index: itemIndex, completed: !completed }, { withCredentials: true });
            fetchData();
        } catch (error) { toast.error('Error updating'); }
    };

    // ============== INVITE LINKS ==============
    const handleCreateInvite = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...inviteForm };
            if (!payload.email) delete payload.email;
            if (!payload.name_hint) delete payload.name_hint;
            if (!payload.department) delete payload.department;
            if (!payload.designation) delete payload.designation;
            if (!payload.is_multi_use) {
                delete payload.is_multi_use;
                delete payload.max_uses;
            } else {
                // Multi-use cannot be tied to email
                delete payload.email;
                delete payload.send_email;
                payload.max_uses = parseInt(payload.max_uses, 10) || 0;
            }
            const { data } = await axios.post(`${API_URL}/api/onboarding-invites`, payload, { withCredentials: true });
            setGeneratedInvite(withShareUrl(data));
            setInviteForm({ email: '', name_hint: '', role: 'employee', department: '', designation: '', expires_in_days: 7, send_email: false, is_multi_use: false, max_uses: 0 });
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate invite link'); }
    };

    const copyInviteLink = (link) => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(link).then(
                () => toast.success('Onboarding link copied!'),
                () => toast.error('Could not copy — please copy manually'),
            );
        } else {
            toast.error('Clipboard not available — copy manually');
        }
    };

    const shareWhatsApp = (invite) => {
        const text = `Hi${invite.name_hint ? ' ' + invite.name_hint : ''}! 👋\n\nWelcome to *DIGI-LOANS*. Click the link below to register and complete your onboarding (takes ~5 mins):\n\n${invite.share_url}\n\n_This link expires in a few days and is single-use._`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareEmail = (invite) => {
        const subject = "You're invited to join DIGI-LOANS";
        const body = `Hi${invite.name_hint ? ' ' + invite.name_hint : ''},\n\nWelcome to DIGI-LOANS! Please click the link below to register and complete your onboarding:\n\n${invite.share_url}\n\nThe link expires soon and can only be used once.\n\n— DIGI-LOANS HR`;
        const to = invite.email || '';
        window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const downloadQR = (invite) => {
        const canvas = document.querySelector(`canvas[data-qr-token="${invite.token}"]`);
        if (!canvas) { toast.error('QR not ready yet'); return; }
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.download = `DIGI-LOANS-Invite-${(invite.name_hint || invite.email || invite.token).replace(/[^a-z0-9]/gi, '_').slice(0, 30)}.png`;
        a.href = url;
        a.click();
        toast.success('QR downloaded');
    };

    const revokeInvite = async (id) => {
        if (!window.confirm('Revoke this invite link? It will become unusable immediately.')) return;
        try {
            await axios.delete(`${API_URL}/api/onboarding-invites/${id}`, { withCredentials: true });
            toast.success('Invite revoked');
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to revoke'); }
    };

    const resendInvite = async (id) => {
        try {
            const { data } = await axios.post(`${API_URL}/api/onboarding-invites/${id}/resend`, {}, { withCredentials: true });
            toast.success(data.message || 'Invite resent');
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to resend'); }
    };

    const getProgressColor = (p) => { if (p >= 100) return 'bg-green-600'; if (p >= 50) return 'bg-brand'; return 'bg-amber-600'; };

    const statusBadge = (s) => {
        const m = {
            pending: { c: 'bg-blue-100 text-blue-800', label: 'Pending' },
            used: { c: 'bg-emerald-100 text-emerald-800', label: 'Used' },
            exhausted: { c: 'bg-purple-100 text-purple-800', label: 'Maxed out' },
            expired: { c: 'bg-slate-200 text-slate-600', label: 'Expired' },
            revoked: { c: 'bg-rose-100 text-rose-700', label: 'Revoked' },
        };
        const v = m[s] || m.pending;
        return <Badge className={`rounded-full ${v.c}`}>{v.label}</Badge>;
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading...</div></div>;

    return (
        <div className="space-y-6" data-testid="onboarding-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Onboarding</h1>
                    <p className="text-slate-500 mt-1">{canManage ? 'Manage employee onboarding workflows + invite links' : 'Your onboarding progress'}</p>
                </div>
                {canManage && (
                    <div className="flex flex-wrap gap-2">
                        <Dialog open={inviteDialog} onOpenChange={(v) => { setInviteDialog(v); if (!v) setGeneratedInvite(null); }}>
                            <DialogTrigger asChild>
                                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow" data-testid="generate-invite-btn">
                                    <Link2 className="h-4 w-4 mr-2" />Share Onboarding Link
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-xl max-w-lg">
                                {!generatedInvite ? (
                                    <>
                                        <DialogHeader>
                                            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }} className="flex items-center gap-2">
                                                <Sparkles className="h-5 w-5 text-emerald-600" />
                                                Generate Onboarding Link
                                            </DialogTitle>
                                            <DialogDescription className="text-xs text-slate-500">
                                                Create a shareable link the new hire can use to register and start onboarding without HR pre-creating their account.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <form onSubmit={handleCreateInvite} className="space-y-4 mt-2">
                                            {/* Single-use vs Common (multi-use) toggle */}
                                            <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-slate-200 rounded-xl p-1 flex" data-testid="invite-mode-toggle">
                                                <button
                                                    type="button"
                                                    onClick={() => setInviteForm({ ...inviteForm, is_multi_use: false, max_uses: 0 })}
                                                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${!inviteForm.is_multi_use ? 'bg-white shadow-sm text-brand' : 'text-slate-500 hover:text-slate-700'}`}
                                                    data-testid="invite-mode-single"
                                                >
                                                    👤 Single-Use Link
                                                    <span className="block text-[10px] font-normal text-slate-400 mt-0.5">For one specific candidate</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setInviteForm({ ...inviteForm, is_multi_use: true, email: '', send_email: false })}
                                                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${inviteForm.is_multi_use ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
                                                    data-testid="invite-mode-multi"
                                                >
                                                    👥 Common Link
                                                    <span className="block text-[10px] font-normal text-slate-400 mt-0.5">Multiple hires register through one link</span>
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{inviteForm.is_multi_use ? 'Link Title (Hint)' : 'Candidate Name (Hint)'}</Label>
                                                    <Input value={inviteForm.name_hint} onChange={(e) => setInviteForm({ ...inviteForm, name_hint: e.target.value })} placeholder={inviteForm.is_multi_use ? 'e.g. Mar 2026 Sales Hiring' : 'e.g. Priya'} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="invite-form-name" />
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email {inviteForm.is_multi_use ? '(disabled for common link)' : '(optional)'}</Label>
                                                    <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="priya@example.com" disabled={inviteForm.is_multi_use} className="rounded-lg mt-1 bg-slate-50 border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="invite-form-email" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</Label>
                                                    <select
                                                        value={inviteForm.role}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1"
                                                        data-testid="invite-form-role"
                                                    >
                                                        <option value="employee">RM (Employee)</option>
                                                        <option value="manager">Manager</option>
                                                        <option value="hr_manager">HR Manager</option>
                                                        <option value="operations_executive">Ops Executive</option>
                                                        <option value="operations_manager">Ops Manager</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Department</Label>
                                                    <Input value={inviteForm.department} onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })} placeholder="Sales" className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="invite-form-dept" />
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Designation</Label>
                                                    <Input value={inviteForm.designation} onChange={(e) => setInviteForm({ ...inviteForm, designation: e.target.value })} placeholder="RM" className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="invite-form-designation" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Expires in (days)</Label>
                                                    <Input type="number" min={1} max={90} value={inviteForm.expires_in_days} onChange={(e) => setInviteForm({ ...inviteForm, expires_in_days: parseInt(e.target.value || '7', 10) })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="invite-form-expires" />
                                                </div>
                                                {inviteForm.is_multi_use ? (
                                                    <div>
                                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Max signups (0 = unlimited)</Label>
                                                        <Input type="number" min={0} max={500} value={inviteForm.max_uses} onChange={(e) => setInviteForm({ ...inviteForm, max_uses: parseInt(e.target.value || '0', 10) })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="invite-form-max-uses" />
                                                    </div>
                                                ) : (
                                                    inviteForm.email && (
                                                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none pb-2">
                                                            <input type="checkbox" checked={inviteForm.send_email} onChange={(e) => setInviteForm({ ...inviteForm, send_email: e.target.checked })} data-testid="invite-form-send-email" />
                                                            Email it to candidate now
                                                        </label>
                                                    )
                                                )}
                                            </div>

                                            {inviteForm.is_multi_use && (
                                                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-xs flex items-start gap-2">
                                                    <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Common link mode:</strong> share this single URL in your team WhatsApp or job posts. Every candidate who clicks will register their own account with the role/department you set above. Cap signups with "Max signups" or revoke anytime.
                                                    </div>
                                                </div>
                                            )}

                                            <DialogFooter className="pt-2">
                                                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setInviteDialog(false)}>Cancel</Button>
                                                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="invite-form-submit">
                                                    <Sparkles className="h-4 w-4 mr-2" />Generate {inviteForm.is_multi_use ? 'Common Link' : 'Link'}
                                                </Button>
                                            </DialogFooter>
                                        </form>
                                    </>
                                ) : (
                                    <>
                                        <DialogHeader>
                                            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }} className="flex items-center gap-2 text-emerald-700">
                                                <Check className="h-5 w-5" />Link Ready to Share
                                            </DialogTitle>
                                            <DialogDescription className="text-xs text-slate-500">
                                                Share this single-use link with the new hire via WhatsApp, Email, or copy & paste.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs break-all text-slate-700 font-mono" data-testid="generated-invite-link">
                                            {generatedInvite.share_url}
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 mt-1">
                                            <Button onClick={() => copyInviteLink(generatedInvite.share_url)} variant="outline" className="rounded-lg" data-testid="copy-link-btn">
                                                <Copy className="h-4 w-4 mr-2" />Copy
                                            </Button>
                                            <Button onClick={() => shareWhatsApp(generatedInvite)} className="bg-[#25D366] hover:bg-[#1ebd57] text-white rounded-lg" data-testid="share-whatsapp-btn">
                                                <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
                                            </Button>
                                            <Button onClick={() => shareEmail(generatedInvite)} variant="outline" className="rounded-lg" data-testid="share-email-btn">
                                                <MailIcon className="h-4 w-4 mr-2" />Email
                                            </Button>
                                        </div>

                                        {/* Scannable QR — for in-person hiring */}
                                        <div className="mt-3 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid="generated-invite-qr">
                                            <div className="bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                                                <QRCodeCanvas
                                                    value={generatedInvite.share_url}
                                                    size={120}
                                                    level="M"
                                                    includeMargin={false}
                                                    data-qr-token={generatedInvite.token}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-bold uppercase tracking-wider">
                                                    <QrCode className="h-3.5 w-3.5" /> Or scan with phone
                                                </div>
                                                <p className="text-sm text-slate-700 font-medium mt-1">Hand the candidate this screen — they scan, register, and start onboarding instantly.</p>
                                                <Button onClick={() => downloadQR(generatedInvite)} size="sm" variant="outline" className="rounded-lg mt-2 text-xs" data-testid="download-qr-btn">
                                                    <Download className="h-3.5 w-3.5 mr-1.5" />Download PNG
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2">
                                            <strong>Role:</strong> {generatedInvite.role?.replace('_', ' ')} {generatedInvite.department && <span> · <strong>Dept:</strong> {generatedInvite.department}</span>} <br />
                                            <strong>Expires:</strong> {generatedInvite.expires_at && new Date(generatedInvite.expires_at).toLocaleDateString()}
                                        </div>
                                        <DialogFooter>
                                            <Button onClick={() => { setGeneratedInvite(null); }} variant="outline" className="rounded-lg">Generate Another</Button>
                                            <Button onClick={() => { setInviteDialog(false); setGeneratedInvite(null); }} className="bg-brand text-white rounded-lg">Done</Button>
                                        </DialogFooter>
                                    </>
                                )}
                            </DialogContent>
                        </Dialog>

                        <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
                            <DialogTrigger asChild><Button variant="outline" className="rounded-lg" data-testid="create-template-btn"><Plus className="h-4 w-4 mr-2" />Template</Button></DialogTrigger>
                            <DialogContent className="rounded-xl">
                                <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Onboarding Template</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Onboard a new hire through the multi-step wizard.</DialogDescription></DialogHeader>
                                <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Template Name</Label>
                                        <Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} className="rounded-lg mt-1" required data-testid="template-name-input" /></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Checklist Items (one per line)</Label>
                                        <textarea value={templateForm.items} onChange={(e) => setTemplateForm({...templateForm, items: e.target.value})} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 min-h-[120px]" placeholder={"Submit ID Proof\nSetup Email\nComplete Training\nMeet Team\nReview Policies"} required data-testid="template-items-input" /></div>
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => setTemplateDialog(false)}>Cancel</Button>
                                        <Button type="submit" className="bg-brand hover:bg-brand rounded-lg" data-testid="save-template-btn">Create Template</Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
                            <DialogTrigger asChild><Button className="bg-brand hover:bg-brand rounded-lg" data-testid="assign-onboarding-btn"><Plus className="h-4 w-4 mr-2" />Assign Onboarding</Button></DialogTrigger>
                            <DialogContent className="rounded-xl">
                                <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Assign Onboarding</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Onboard a new hire through the multi-step wizard.</DialogDescription></DialogHeader>
                                <form onSubmit={handleAssign} className="space-y-4 mt-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee</Label>
                                        <select value={assignForm.employee_id} onChange={(e) => setAssignForm({...assignForm, employee_id: e.target.value})} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1" required>
                                            <option value="">Select Employee</option>
                                            {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                                        </select></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Template</Label>
                                        <select value={assignForm.template_id} onChange={(e) => setAssignForm({...assignForm, template_id: e.target.value})} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1" required>
                                            <option value="">Select Template</option>
                                            {templates.map(t => <option key={t._id} value={t._id}>{t.name} ({t.items?.length} items)</option>)}
                                        </select></div>
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => setAssignDialog(false)}>Cancel</Button>
                                        <Button type="submit" className="bg-brand hover:bg-brand rounded-lg" data-testid="confirm-assign-btn">Assign</Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            {canManage && summary && (
                <div className="grid grid-cols-3 gap-4">
                    <Card className="rounded-xl border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Total</p><p className="text-2xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.total}</p></CardContent></Card>
                    <Card className="rounded-xl border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">In Progress</p><p className="text-2xl font-black text-brand mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.in_progress}</p></CardContent></Card>
                    <Card className="rounded-xl border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Completed</p><p className="text-2xl font-black text-green-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.completed}</p></CardContent></Card>
                </div>
            )}

            {canManage ? (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-white border border-slate-200 rounded-full p-1 shadow-sm">
                        <TabsTrigger value="records" className="rounded-full text-xs sm:text-sm" data-testid="tab-records">Records</TabsTrigger>
                        <TabsTrigger value="invites" className="rounded-full text-xs sm:text-sm" data-testid="tab-invites">
                            Invite Links {invites.filter((i) => i.status === 'pending').length > 0 && (
                                <span className="ml-2 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                    {invites.filter((i) => i.status === 'pending').length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="records" className="mt-4">
                        <RecordsTable records={records} expandedRecord={expandedRecord} setExpandedRecord={setExpandedRecord} toggleItem={toggleItem} getProgressColor={getProgressColor} />
                    </TabsContent>

                    <TabsContent value="invites" className="mt-4">
                        <Card className="rounded-xl border-slate-200">
                            <CardHeader className="border-b border-slate-200">
                                <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Link2 className="h-5 w-5 text-emerald-600" />Onboarding Invite Links
                                    <span className="ml-auto text-xs font-normal text-slate-500">{invites.length} total</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {invites.length === 0 ? (
                                    <div className="p-10 text-center">
                                        <Link2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm">No invite links yet</p>
                                        <p className="text-slate-400 text-xs mt-1">Click <strong>Share Onboarding Link</strong> above to generate one for your next hire.</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-100">
                                        {invites.map((inv) => (
                                            <div key={inv._id} className="p-4 hover:bg-slate-50 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`invite-row-${inv._id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium text-slate-900 text-sm">{inv.name_hint || inv.email || 'Unnamed candidate'}</p>
                                                        {statusBadge(inv.status)}
                                                        {inv.is_multi_use && <Badge className="rounded-full bg-emerald-100 text-emerald-700 text-[10px]">👥 Common</Badge>}
                                                        <Badge className="rounded-full bg-slate-100 text-slate-600 text-[10px]">{(inv.role || 'employee').replace('_', ' ')}</Badge>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                                                        {inv.email && <span>📧 {inv.email} · </span>}
                                                        {inv.department && <span>🏢 {inv.department}</span>}
                                                    </p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                                        Created {new Date(inv.created_at).toLocaleDateString()} · {
                                                            inv.is_multi_use
                                                                ? `${inv.use_count || 0}${inv.max_uses ? '/' + inv.max_uses : ''} signups · expires ${inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}`
                                                                : (inv.status === 'used' && inv.used_by_name)
                                                                    ? `Used by ${inv.used_by_name} (${inv.used_by_employee_id})`
                                                                    : `Expires ${inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}`
                                                        }
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {(inv.status === 'pending' || (inv.is_multi_use && inv.status !== 'revoked' && inv.status !== 'expired' && inv.status !== 'exhausted')) && (
                                                        <>
                                                            <Button size="sm" variant="outline" onClick={() => copyInviteLink(inv.share_url)} className="rounded-lg" data-testid={`copy-${inv._id}`}>
                                                                <Copy className="h-3.5 w-3.5 mr-1" />Copy
                                                            </Button>
                                                            <Button size="sm" variant="outline" onClick={() => setQrViewInvite(inv)} className="rounded-lg" data-testid={`qr-${inv._id}`}>
                                                                <QrCode className="h-3.5 w-3.5 mr-1" />QR
                                                            </Button>
                                                            <Button size="sm" onClick={() => shareWhatsApp(inv)} className="bg-[#25D366] hover:bg-[#1ebd57] text-white rounded-lg" data-testid={`whatsapp-${inv._id}`}>
                                                                <MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp
                                                            </Button>
                                                            {inv.email && !inv.is_multi_use && (
                                                                <Button size="sm" variant="outline" onClick={() => resendInvite(inv._id)} className="rounded-lg" data-testid={`resend-${inv._id}`}>
                                                                    <Send className="h-3.5 w-3.5 mr-1" />Resend
                                                                </Button>
                                                            )}
                                                            <Button size="sm" variant="outline" onClick={() => revokeInvite(inv._id)} className="rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" data-testid={`revoke-${inv._id}`}>
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            ) : (
                <RecordsTable records={records} expandedRecord={expandedRecord} setExpandedRecord={setExpandedRecord} toggleItem={toggleItem} getProgressColor={getProgressColor} />
            )}

            {/* Standalone QR viewer for existing pending invites */}
            <Dialog open={!!qrViewInvite} onOpenChange={(v) => !v && setQrViewInvite(null)}>
                <DialogContent className="rounded-xl max-w-sm">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }} className="flex items-center gap-2 text-emerald-700">
                            <QrCode className="h-5 w-5" />Scan to Onboard
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Hand this screen to <strong>{qrViewInvite?.name_hint || qrViewInvite?.email || 'the candidate'}</strong>. They scan, register, and start onboarding instantly.
                        </DialogDescription>
                    </DialogHeader>
                    {qrViewInvite && (
                        <div className="flex flex-col items-center py-2">
                            <div className="bg-white p-4 rounded-2xl border-2 border-slate-200 shadow-md" data-testid="qr-view-canvas">
                                <QRCodeCanvas
                                    value={qrViewInvite.share_url}
                                    size={220}
                                    level="M"
                                    includeMargin={false}
                                    data-qr-token={qrViewInvite.token}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 mt-3 break-all text-center px-2">{qrViewInvite.share_url}</p>
                            <div className="flex gap-2 mt-4 w-full">
                                <Button onClick={() => copyInviteLink(qrViewInvite.share_url)} variant="outline" className="rounded-lg flex-1" data-testid="qr-view-copy">
                                    <Copy className="h-3.5 w-3.5 mr-1.5" />Copy
                                </Button>
                                <Button onClick={() => downloadQR(qrViewInvite)} className="bg-brand text-white rounded-lg flex-1" data-testid="qr-view-download">
                                    <Download className="h-3.5 w-3.5 mr-1.5" />Save PNG
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

const RecordsTable = ({ records, expandedRecord, setExpandedRecord, toggleItem, getProgressColor }) => (
    <Card className="rounded-xl border-slate-200">
        <CardHeader className="border-b border-slate-200">
            <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <ClipboardList className="h-5 w-5 text-brand" />Onboarding Records
            </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
                {records.map(r => (
                    <div key={r._id} data-testid={`onboarding-${r._id}`}>
                        <div className="p-4 hover:bg-slate-50 cursor-pointer flex items-center gap-4" onClick={() => setExpandedRecord(expandedRecord === r._id ? null : r._id)}>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-slate-500" />
                                    <p className="font-medium text-slate-900">{r.employee_name}</p>
                                    <Badge className={`rounded-full ${r.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{r.status?.replace('_', ' ')}</Badge>
                                </div>
                                <p className="text-sm text-slate-500 mt-0.5">{r.template_name} - {r.checklist?.length} tasks</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                                    <div className={`h-full ${getProgressColor(r.progress)} transition-all`} style={{ width: `${r.progress}%` }} />
                                </div>
                                <span className="text-sm font-bold text-slate-700 w-10">{r.progress}%</span>
                                <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expandedRecord === r._id ? 'rotate-90' : ''}`} />
                            </div>
                        </div>
                        {expandedRecord === r._id && (
                            <div className="px-4 pb-4 bg-slate-50">
                                <div className="space-y-1">
                                    {r.checklist?.map((item, i) => (
                                        <div key={`${r._id}-${i}`} className="flex items-center gap-3 py-2 px-3 bg-white border border-slate-100 rounded-lg">
                                            <button
                                                onClick={() => toggleItem(r._id, i, item.completed)}
                                                className={`w-5 h-5 flex items-center justify-center rounded-md border-2 transition-colors ${item.completed ? 'bg-green-600 border-green-600' : 'border-slate-300 hover:border-blue-400'}`}
                                                data-testid={`checklist-item-${r._id}-${i}`}
                                            >
                                                {item.completed && <Check className="h-3 w-3 text-white" />}
                                            </button>
                                            <span className={`text-sm flex-1 ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.item || item.task || `Task ${i + 1}`}</span>
                                            {item.completed_at && <span className="text-[10px] text-slate-400">{new Date(item.completed_at).toLocaleDateString()}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {records.length === 0 && <div className="p-8 text-center text-slate-500">No onboarding records yet</div>}
            </div>
        </CardContent>
    </Card>
);

export default Onboarding;
